import { AsyncPipe, NgClass, NgFor, NgIf } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Inject,
  Input,
  Optional,
  Output,
  inject,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyBadgeModule } from '@vendasta/galaxy/badge';
import { BehaviorSubject, map, of, switchMap } from 'rxjs';
import { ConversationService } from '../../../../../../core/src/lib/conversation.service';
import { InboxService } from '../../../../../../core/src/lib/inbox.service';
import {
  ConversationUnseen,
  KabobDynamicButton,
} from '../../../../../../core/src/lib/interface/conversation.interface';
import { HostAppInterface } from '../../../../../../core/src/lib/interface/host-app.interface';
import {
  CONVERSATION_HOST_APP_INTERFACE_TOKEN,
  CONVERSATION_ROUTES_TOKEN,
} from '../../../../../../core/src/lib/tokens';
import { ViewModeService } from '../../../../../../core/src/lib/view-mode.service';
import { InboxConversationAssigneeComponent } from '../../../../components/inbox-conversation-assignee/inbox-conversation-assignee.component';
import { InboxConversationSubtitleAccountComponent } from '../../../../components/inbox-conversation-subtitle/inbox-conversation-subtitle.component';
import { InboxConversationTitleComponent } from '../../../../components/inbox-conversation-title/inbox-conversation-title.component';
import { INBOX_NAVIGATION_LINK } from '../../../../constants';
import { InboxInfoDrawerService } from '../inbox-info-pane/inbox-info-drawer.service';

@Component({
  selector: 'inbox-messages-top-bar',
  templateUrl: './inbox-messages-top-bar.component.html',
  styleUrls: ['./inbox-messages-top-bar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    // Common
    AsyncPipe,
    NgFor,
    NgIf,
    NgClass,

    // Material
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatTooltipModule,

    // Everything else
    GalaxyBadgeModule,
    InboxConversationAssigneeComponent,
    InboxConversationTitleComponent,
    TranslateModule,
    InboxConversationSubtitleAccountComponent,
  ],
})
export class InboxMessagesTopBarComponent {
  conversationDetail$ = this.conversationService.currentConversationDetail$;
  kabobAvailableActions$ = this.conversationDetail$.pipe(
    switchMap((conversationDetail) => {
      if (!conversationDetail) return of(new Array<KabobDynamicButton>());
      return this.hostAppInterface.getKabobAvailableActions(conversationDetail);
    }),
  );

  isInternalInfoDeleted = false;
  unseen$$ = new BehaviorSubject<ConversationUnseen>({ read: true, conversationId: '' });
  unseen$ = this.unseen$$.asObservable();

  readonly showInboxViews$ = this.inboxService.showInboxViews$;

  // readonly isBusinessApp = true;
  readonly isBusinessApp = this.inboxService.isBusinessApp;

  // only display assignee saleperson for sales center and partner center for now
  readonly displayAssignee = this.inboxService.isSalesCenter || this.inboxService.isPartnerCenter;

  @Input() set unseen(value: ConversationUnseen) {
    this.unseen$$.next(value);
  }
  @Input() isFollowing = false;
  @Input() isOpen = false;

  useSidebarStyles$ = this.viewModeService.viewMode$.pipe(map((viewMode) => viewMode === 'sidebar'));

  @Input() accountGroupId = '';
  @Input() groupId = '';

  @Output() markedUnread = new EventEmitter();
  @Output() changeFollowStatus = new EventEmitter<boolean>();
  @Output() changeOpenStatus = new EventEmitter<boolean>();

  private readonly routes = toSignal(inject(CONVERSATION_ROUTES_TOKEN));

  constructor(
    private router: Router,
    private conversationService: ConversationService,
    private inboxService: InboxService,
    @Optional()
    private viewModeService: ViewModeService,
    private inboxInfoDrawerService: InboxInfoDrawerService,
    @Inject(CONVERSATION_HOST_APP_INTERFACE_TOKEN) private readonly hostAppInterface: HostAppInterface,
  ) {}

  back(): void {
    if (this.routes()?.useModal) {
      this.conversationService.removeLastConversationId();

      this.router.navigate(INBOX_NAVIGATION_LINK, {
        queryParamsHandling: 'merge',
        queryParams: { inboxPrefilledMessage: null },
      });
      return;
    }

    if (this.accountGroupId) {
      this.router.navigateByUrl(`/account/location/${this.accountGroupId}/inbox`);
    } else if (this.groupId) {
      this.router.navigateByUrl(`/account/brands/${this.groupId}/inbox`);
    } else {
      this.router.navigateByUrl(`/inbox`);
    }
  }

  markUnread(): void {
    this.markedUnread.emit({
      read: false,
    });
  }

  markRead(): void {
    this.markedUnread.emit({
      read: true,
    });
  }

  updateFollowStatus(): void {
    this.changeFollowStatus.emit(!this.isFollowing);
  }

  toggleDetailsDrawer() {
    this.inboxInfoDrawerService.toggle();
  }

  toggleConversationOpen(): void {
    this.changeOpenStatus.emit(!this.isOpen);
  }
}
